/**
 * A game panel that floats free of the bar.
 *
 * The bar holds one panel at a time above the buttons, which is right on a phone
 * and cramped on a monitor: reading your skills while your bag is open is not an
 * unreasonable thing to want. Turned on, each panel becomes one of these instead,
 * dragged wherever it is wanted and staying there between sessions.
 *
 * The same window behaviour the bank and the shop already use, so a panel moves,
 * remembers and stays on screen exactly the way those do.
 */

import type { ReactNode } from 'react';
import { CloseButton } from './CloseButton';
import { useDraggableWindow } from './draggable';
import { useAltHeld } from './alt-mode';
import { useWindowGrips } from './grips';

export function FloatingPanel({
  kind,
  title,
  note,
  home,
  depth,
  onRaise,
  onClose,
  children,
}: {
  /** What it is, which is also where its position is remembered. */
  kind: string;
  title: string;
  /** A count or a word beside the title, when there is one worth showing. */
  note?: ReactNode;
  /** Where it opens the first time, before anybody has moved it. */
  home?: (size: { width: number; height: number }) => { x: number; y: number };
  /** How high it sits over the others, which is what makes these windows. */
  depth?: number;
  /** Touched: bring it to the front. */
  onRaise?: () => void;
  /** Left out when the panel is up for a reason of its own and cannot be shut. */
  onClose?: () => void;
  children: ReactNode;
}): JSX.Element {
  const { attach, style, onPointerDown, nudge } = useDraggableWindow(`panel.${kind}`, { home });

  // Held down, Alt puts the interface into a mode where it can be rearranged,
  // and that is when a window shows what it can be taken hold of by.
  const altHeld = useAltHeld();
  const { size: windowSize, grips } = useWindowGrips(`panel.${kind}`, nudge, altHeld);

  return (
    <div
      className={`panel-card floating${altHeld ? ' ui-mode' : ''}${windowSize ? ' sized' : ''}`}
      ref={attach}
      style={{ ...style, ...(windowSize ?? {}), ...(depth === undefined ? {} : { zIndex: depth }) }}
      // Anywhere on it, not only its bar: clicking into a panel that is partly
      // behind another should bring it forward, which is what every window does
      // everywhere else.
      onPointerDown={() => onRaise?.()}
    >
      <div
        className="panel-head"
        onPointerDown={(event) => {
          onRaise?.();
          onPointerDown(event);
        }}
      >
        <span>{title}</span>
        {note !== undefined ? <span className="panel-count">{note}</span> : null}
        {onClose ? <CloseButton onClose={onClose} /> : null}
      </div>
      {/* What scrolls is the inside, never the window itself. The grips belong
          to the window: inside something that scrolls they scrolled away with
          the contents and, sitting on its edge, counted as more to scroll to,
          so holding the key to resize a panel gave it a scrollbar it did not
          need and could not be sized past. */}
      <div className="floating-body">{children}</div>
      {grips}
    </div>
  );
}
